/**
 * WidgetCard — chrome for every analytics widget.
 *
 * Mirrors GoodData's per-widget shell: title, optional info-tooltip, ellipsis
 * menu with Export to XLSX / Export to CSV / Alerts subscription. Wraps the
 * widget body (a Chart, a Table, a KpiWithDelta, etc.).
 *
 * MVP scope (July 1):
 *   - Export to CSV: wired when `onExportCsv` prop is provided
 *   - Export to XLSX: disabled "Coming soon" item (v0.7.0)
 *   - Alerts subscription: disabled "Coming soon" item (cross-cuts with the
 *     Smart Alerting deep capability — v0.7.0)
 *
 * The widget body should not render its own Card chrome — WidgetCard provides it.
 */

import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { MoreVertical, FileSpreadsheet, FileDown, Bell } from '@/utils/icons';
import { useChartLib } from '@/components/charts/useChartLib';
import { DEFAULT_CHART_LIB } from '@/components/charts/types';

interface WidgetCardProps {
  title: string;
  /** Optional info tooltip shown next to the title. */
  info?: string;
  /** Optional CSV exporter — when supplied, the "Export to CSV" item is enabled. */
  onExportCsv?: () => void;
  /** Optional click handler for the entire card (e.g., navigate to a detail page). */
  onClick?: () => void;
  /** Widget body. */
  children: ReactNode;
  /** Render a custom action area to the right of the title (replaces the default menu). */
  actions?: ReactNode;
  /** Set true to suppress the standard ellipsis menu. */
  hideMenu?: boolean;
  className?: string;
  /** Test id. */
  'data-testid'?: string;
}

export function WidgetCard({
  title,
  info,
  onExportCsv,
  onClick,
  children,
  actions,
  hideMenu,
  className,
  'data-testid': testId,
}: WidgetCardProps) {
  // Slicer feedback — when a dev has selected a non-default chart library
  // via the Dev Console slicer, render a small chip in the card header so
  // it's obvious which library is active. The badge sits tightly in the
  // top-right action area, before the ellipsis menu, and never overlaps
  // chart content. Default state ('recharts') shows nothing.
  const [chartLib] = useChartLib();
  const showLibBadge = chartLib !== DEFAULT_CHART_LIB;
  const libLabel = chartLib.charAt(0).toUpperCase() + chartLib.slice(1);

  return (
    <Card className={className} data-testid={testId} onClick={onClick}>
      <CardHeader className="dls-flex dls-flex-row dls-items-start dls-justify-between dls-space-y-0 dls-pb-2">
        <CardTitle className="dls-flex dls-items-center dls-gap-1.5 dls-text-base dls-font-semibold">
          {title}
          {info && <InfoTooltip text={info} />}
        </CardTitle>
        <div className="dls-flex dls-items-center dls-gap-1.5" data-pdf-exclude="true">
          {showLibBadge && (
            <span
              aria-hidden
              title={`Rendered via ${libLabel}`}
              className="dls-inline-flex dls-items-center dls-rounded dls-bg-neeve-primary/10 dls-text-neeve-primary dls-border dls-border-neeve-primary/25 dls-px-1.5 dls-py-0.5 dls-text-[10px] dls-font-semibold dls-tracking-wider dls-uppercase dls-leading-none"
            >
              {libLabel}
            </span>
          )}
          {actions}
          {!hideMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="dls-h-7 dls-w-7 dls-text-muted-foreground hover:dls-text-foreground"
                  aria-label="Widget options"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="dls-h-4 dls-w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="dls-w-56">
                <DropdownMenuLabel>Export</DropdownMenuLabel>
                <DropdownMenuItem disabled className="dls-gap-2">
                  <FileSpreadsheet className="dls-h-4 dls-w-4" />
                  Export to XLSX
                  <span className="dls-ml-auto dls-text-[10px] dls-text-muted-foreground">Coming soon</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={!onExportCsv}
                  onSelect={() => onExportCsv?.()}
                  className="dls-gap-2"
                >
                  <FileDown className="dls-h-4 dls-w-4" />
                  Export to CSV
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled className="dls-gap-2">
                  <Bell className="dls-h-4 dls-w-4" />
                  Alerts
                  <span className="dls-ml-auto dls-text-[10px] dls-text-muted-foreground">Deep Insights</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
